import { useState, useCallback } from 'react';
import { IngredientDetail } from '../types';
import { dataService } from '@foodbook/core-logic';

interface UseIngredientsProps {
  initialIngredients: IngredientDetail[];
}

interface UseIngredientsReturn {
  // State
  ingredients: IngredientDetail[];
  setIngredients: (ingredients: IngredientDetail[] | ((prev: IngredientDetail[]) => IngredientDetail[])) => void;

  // Handlers
  handleToggleIngredientFavorite: (ingredientId: string, favoriteIngredientIds: string[], setFavoriteIngredientIds: (ids: string[]) => void) => Promise<void>;
  handleFindRecipe: (ingredientName: string, navigate: (view: string, params?: any) => void) => void;
}

export const useIngredients = ({ initialIngredients }: UseIngredientsProps): UseIngredientsReturn => {
  const [ingredients, setIngredientsInternal] = useState<IngredientDetail[]>(initialIngredients);

  // Keep this setter state-only. Public ingredient persistence should be explicit
  // so hydration and pagination flows cannot overwrite production data.
  const setIngredients = useCallback((newIngredients: IngredientDetail[] | ((prev: IngredientDetail[]) => IngredientDetail[])) => {
    setIngredientsInternal(prev => {
      return typeof newIngredients === 'function' ? newIngredients(prev) : newIngredients;
    });
  }, []);

  // Handle Ingredient Favorite Toggle - with cloud persistence
  const handleToggleIngredientFavorite = useCallback(async (ingredientId: string, favoriteIngredientIds: string[], setFavoriteIngredientIds: (ids: string[]) => void) => {
    const isFav = favoriteIngredientIds.includes(ingredientId);

    // Optimistically update local state
    setFavoriteIngredientIds(
      isFav
        ? favoriteIngredientIds.filter(id => id !== ingredientId)
        : [...favoriteIngredientIds, ingredientId]
    );

    // Persist to cloud
    try {
      await dataService.toggleFavorite('ingredient', ingredientId);
    } catch (error) {
      console.error('[useIngredients] Failed to sync favorite to cloud:', error);
      // Revert on error
      setFavoriteIngredientIds(
        isFav
          ? [...favoriteIngredientIds, ingredientId]
          : favoriteIngredientIds.filter(id => id !== ingredientId)
      );
    }
  }, []);

  // Handle Find Recipe
  const handleFindRecipe = useCallback((ingredientName: string, navigate: ((view: string, params?: any) => void) | ((path: string) => void)) => {
    // Support both old navigation and react-router navigate
    if (ingredientName) {
      navigate(`/recipes?search=${encodeURIComponent(ingredientName)}`);
    } else {
      navigate('/recipes');
    }
  }, []);

  return {
    ingredients,
    setIngredients,
    handleToggleIngredientFavorite,
    handleFindRecipe,
  };
};
